/** @format */

import { format, parse } from "date-fns";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBoxOpen,
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaEye,
  FaShoppingBag,
  FaTimesCircle,
} from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { get_dashboard_index_data } from "../../store/reducers/dashboardReducer";
import { devise, ExpirationTimer } from "../../utils/utils";

// ─── Badge Maps (identiques à MyOrders) ──────────────────────────────────────

const PAYMENT_BADGE = {
  paid: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    ring: "ring-emerald-200",
    dot: "bg-emerald-500",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  unpaid: {
    bg: "bg-red-50",
    text: "text-red-700",
    ring: "ring-red-200",
    dot: "bg-red-500",
  },
  default: {
    bg: "bg-slate-50",
    text: "text-slate-600",
    ring: "ring-slate-200",
    dot: "bg-slate-400",
  },
};

const DELIVERY_BADGE = {
  delivered: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    ring: "ring-emerald-200",
    dot: "bg-emerald-500",
  },
  placed: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    ring: "ring-blue-200",
    dot: "bg-blue-500",
  },
  warehouse: {
    bg: "bg-violet-50",
    text: "text-violet-700",
    ring: "ring-violet-200",
    dot: "bg-violet-500",
  },
  cancelled: {
    bg: "bg-red-50",
    text: "text-red-700",
    ring: "ring-red-200",
    dot: "bg-red-500",
  },
  pending: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    ring: "ring-amber-200",
    dot: "bg-amber-500",
  },
  default: {
    bg: "bg-slate-50",
    text: "text-slate-600",
    ring: "ring-slate-200",
    dot: "bg-slate-400",
  },
};

// ─── Sub-components (partagés avec MyOrders) ─────────────────────────────────

const StatusBadge = ({ value, map }) => {
  const style = map[value?.toLowerCase()] || map.default;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${style.bg} ${style.text} ${style.ring}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
      {value ?? "—"}
    </span>
  );
};

const ViewButton = ({ orderId, label }) => (
  <Link
    to={`/dashboard/order/details/${orderId}`}
    className='
      inline-flex items-center gap-1.5 px-3 py-1.5
      bg-white border border-slate-200 text-slate-700
      rounded-lg text-xs font-medium
      hover:border-slate-400 hover:bg-slate-50 hover:shadow-sm
      transition-all duration-150
    '
    title={label}
  >
    <FaEye className='text-slate-500 text-[11px]' />
    {label}
  </Link>
);

const PayButton = ({ onClick, label }) => (
  <button
    type='button'
    onClick={onClick}
    className='
      inline-flex items-center gap-1.5 px-3 py-1.5
      bg-blue-600 text-white
      rounded-lg text-xs font-semibold
      hover:bg-blue-700 hover:shadow-md hover:shadow-blue-200
      active:scale-95
      transition-all duration-150
    '
    title={label}
  >
    <FaCreditCard className='text-[11px]' />
    {label}
  </button>
);

const EmptyState = ({ t }) => (
  <tr>
    <td colSpan={6}>
      <div className='flex flex-col items-center justify-center py-14 text-slate-400 gap-3'>
        <FaBoxOpen className='text-4xl text-slate-300' />
        <p className='text-sm font-medium'>{t("No orders found")}</p>
      </div>
    </td>
  </tr>
);

// ─── Stat Card ────────────────────────────────────────────────────────────────

const StatCard = ({
  icon: Icon,
  iconBg,
  iconColor,
  value,
  label,
  as: Tag = "div",
  to,
}) => {
  const inner = (
    <div className='flex items-center gap-4 p-5 bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-shadow duration-200 w-full'>
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}
      >
        <Icon className={`text-xl ${iconColor}`} />
      </div>
      <div className='min-w-0'>
        <p className='text-2xl font-bold text-slate-800 leading-tight'>
          {value}
        </p>
        <p className='text-xs text-slate-500 mt-0.5 truncate'>{label}</p>
      </div>
    </div>
  );

  if (Tag === Link) {
    return (
      <Link to={to} className='block'>
        {inner}
      </Link>
    );
  }
  return inner;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Index = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { customerInfo } = useSelector((state) => state.auth);
  const { totalOrder, cancelledOrder, pendingOrder, recentOrders } =
    useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(get_dashboard_index_data(customerInfo.id));
  }, [customerInfo.id, dispatch]);

  const formatDate = useCallback((dateString) => {
    try {
      const parsed = parse(dateString, "MMMM d, yyyy hh:mm a", new Date());
      return format(parsed, "MMM. d, yyyy");
    } catch {
      return dateString ?? "—";
    }
  }, []);

  const redirect = useCallback(
    (order) => {
      navigate("/payment", {
        state: {
          price: order.price,
          items: order.quantity,
          orderId: order._id,
        },
      });
    },
    [navigate],
  );

  const TABLE_COLS = [
    t("Order ID"),
    t("Price"),
    t("Payment Status"),
    t("Order Status"),
    "Date",
    "Actions",
  ];

  return (
    <div className='flex flex-col gap-6'>
      {/* ── Stat Cards ── */}
      <div className='grid grid-cols-4 md:grid-cols-1 gap-5'>
        <StatCard
          icon={FaShoppingBag}
          iconBg='bg-blue-50'
          iconColor='text-blue-600'
          value={totalOrder}
          label={t("Total Orders")}
        />
        <StatCard
          icon={FaClock}
          iconBg='bg-amber-50'
          iconColor='text-amber-600'
          value={pendingOrder}
          label={t("Pending Orders")}
        />
        <StatCard
          icon={FaTimesCircle}
          iconBg='bg-red-50'
          iconColor='text-red-600'
          value={cancelledOrder}
          label={t("Cancelled Orders")}
        />
        <StatCard
          as={Link}
          to='/dashboard/orders'
          icon={FaCheckCircle}
          iconBg='bg-emerald-50'
          iconColor='text-emerald-600'
          value={t("View All")}
          label={t("Paid And Delivered")}
        />
      </div>

      {/* ── Recent Orders ── */}
      <div className='bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden'>
        {/* Header */}
        <div className='flex items-center gap-3 px-6 py-5 border-b border-slate-100'>
          <h2 className='text-lg font-bold text-slate-800'>
            {t("Recent Orders")}
          </h2>
          {recentOrders?.length > 0 && (
            <span className='bg-slate-100 text-slate-600 text-xs font-semibold px-2.5 py-0.5 rounded-full'>
              {recentOrders.length}
            </span>
          )}
        </div>

        {/* Table */}
        <div className='overflow-x-auto'>
          <table className='w-full text-sm text-left'>
            <thead className='bg-slate-50 border-b border-slate-100'>
              <tr>
                {TABLE_COLS.map((col) => (
                  <th
                    key={col}
                    scope='col'
                    className='px-5 py-3.5 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap'
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className='divide-y divide-slate-50'>
              {!recentOrders?.length ? (
                <EmptyState t={t} />
              ) : (
                recentOrders.map((order) => (
                  <tr
                    key={order._id}
                    className='hover:bg-slate-50/70 transition-colors duration-100'
                  >
                    {/* Order ID */}
                    <td className='px-5 py-4 whitespace-nowrap'>
                      <span className='font-mono text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-0.5 rounded'>
                        #{order._id?.slice(-8).toUpperCase()}
                      </span>
                    </td>

                    {/* Price */}
                    <td className='px-5 py-4 whitespace-nowrap'>
                      <span className='font-bold text-slate-800'>
                        {order.price?.toFixed(2)}
                      </span>{" "}
                      <span className='text-xs text-slate-400'>{devise}</span>
                    </td>

                    {/* Payment Status */}
                    <td className='px-5 py-4 whitespace-nowrap'>
                      <StatusBadge
                        value={order.payment_status}
                        map={PAYMENT_BADGE}
                      />
                    </td>

                    {/* Delivery Status */}
                    <td className='px-5 py-4 whitespace-nowrap'>
                      <StatusBadge
                        value={order.delivery_status}
                        map={DELIVERY_BADGE}
                      />
                    </td>

                    {/* Date */}
                    <td className='px-5 py-4 whitespace-nowrap text-slate-500 text-xs'>
                      {formatDate(order.date)}
                    </td>

                    {/* Actions */}
                    <td className='px-5 py-4 whitespace-nowrap'>
                      <div className='flex items-center gap-2'>
                        <ViewButton orderId={order._id} label={t("View")} />

                        {order.payment_status !== "paid" && (
                          <PayButton
                            onClick={() => redirect(order)}
                            label={t("Pay Now")}
                          />
                        )}

                        {order.expiresAt && (
                          <div className='mt-0.5'>
                            <ExpirationTimer expiresAt={order.expiresAt} />
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Footer link */}
        <div className='flex justify-end px-5 py-4 border-t border-slate-100'>
          <Link
            to='/dashboard/my_orders'
            className='text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150'
          >
            {t("View all orders")} &rarr;
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Index;
